/**
 * Public GG+ pass page. Opening it previews the link. Claim is a button.
 * Logged-out visitors start signup and return here to finish. Login is the
 * "Log in instead" link on the email step.
 *
 * An open link is the gift landing. The other outcomes stay a short card.
 */
import React from 'react';
import '../../GG_plus/Gg_plus_shared.css';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import GGApi from '../../../utils/GGApi';
import authServices from '../../../utils/authServices';
import GG_loader from '../../general_components/GG_loader/GG_loader';
import MasterButton from '../../general_components/MasterButton/MasterButton';
import Plus_pass_offer from './Plus_pass_offer';
import { formatMembershipDate } from '../../../utils/membershipUrl';
import { consumeReturnAfterAuth, peekReturnAfterAuth } from '../../../utils/safeRedirect';
import {
  clearClaimPending,
  isPassPath,
  peekClaimPending,
  rememberClaimPending,
  rememberPassToken,
} from '../../../utils/ggPlusPassClaim';
import gg_plus_logo from '../../../images/logos/gg_plus/gg_plus_103.png';
import {
  logGgPlusPassClaimStarted,
  logGgPlusPassOpened,
  logGgPlusPassRedeemed,
  logGgPlusPassRedemptionFailed,
} from '../../../utils/ggPlusAnalytics';

function passAnalytics(source) {
  if (!source) return {};
  return {
    pass_id: source.passId,
    source_type: source.sourceType,
    source_id: source.sourceId,
    referrer_user_id: source.referrerUserId,
    duration_months: source.durationMonths,
  };
}

function registryAction(plans) {
  if (!plans || !plans.length) {
    return { text: 'Set up your registry', path: '/add_account' };
  }
  if (plans.length === 1) {
    return { text: 'Open your registry', path: `/plan/${plans[0].id}` };
  }
  return { text: 'Open your registries', path: '/plan' };
}

class Plus_pass_page extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      phase: 'loading',
      durationMonths: 3,
      sourceType: null,
      sourceId: null,
      referrerUserId: null,
      passId: null,
      giverLabel: null,
      freeUntil: null,
      adjustmentType: null,
      registry: null,
    };
    this.handleClaim = this.handleClaim.bind(this);
    this.goToRegistry = this.goToRegistry.bind(this);
  }

  token() {
    return (this.props.match && this.props.match.params && this.props.match.params.token) || '';
  }

  componentDidMount() {
    this.openPass();
  }

  loggedIn() {
    const user = authServices.storedUser();
    return !!(user && user.accessToken);
  }

  /** Drop a stored signup return once this page is the place they came back to. */
  clearStoredReturn() {
    const token = this.token();
    const stored = peekReturnAfterAuth();
    if (stored && isPassPath(stored) && stored.indexOf(token) !== -1) {
      consumeReturnAfterAuth();
    }
  }

  openPass() {
    const token = this.token();
    if (!token) {
      this.setState({ loading: false, phase: 'unavailable' });
      return;
    }

    // Keep the token across the multi-step signup even if a screen drops the URL.
    rememberPassToken(token);
    this.clearStoredReturn();

    // They already tapped Claim. Finish it without asking again.
    if (this.loggedIn() && peekClaimPending() === token) {
      clearClaimPending();
      this.redeem();
      return;
    }

    this.loadPreview();
  }

  loadPreview() {
    const token = this.token();
    this.setState({ loading: true });
    GGApi.preview_gg_plus_pass(token)
      .then((preview) => {
        if (!preview || preview.available === false) {
          logGgPlusPassOpened(passAnalytics(preview));
          this.setState({ loading: false, phase: 'unavailable' });
          return;
        }
        logGgPlusPassOpened(passAnalytics(preview));
        const next = {
          loading: false,
          durationMonths: preview.durationMonths,
          passId: preview.passId || null,
          sourceType: preview.sourceType || null,
          sourceId: preview.sourceId || null,
          referrerUserId: preview.referrerUserId || null,
          giverLabel: preview.giverLabel || null,
          freeUntil: preview.freeUntil || null,
        };
        if (preview.alreadyClaimed) {
          next.phase = 'already';
        } else if (preview.selfClaim) {
          next.phase = 'self';
        } else {
          next.phase = 'offer';
        }
        this.setState(next, () => {
          if (preview.alreadyClaimed) this.loadRegistry();
        });
      })
      .catch(() => this.setState({ loading: false, phase: 'error' }));
  }

  redeem() {
    const token = this.token();
    this.setState({ loading: true, phase: 'claiming' });
    GGApi.redeem_gg_plus_pass(token)
      .then((result) => {
        const outcome = result && result.outcome;
        if (outcome === 'redeemed' || outcome === 'already_claimed') {
          // Refresh entitlements before the rest of the app reads GG+.
          return GGApi.checkToken().then(() => result);
        }
        return result;
      })
      .then((result) => {
        const outcome = result && result.outcome;
        if (outcome === 'redeemed') {
          logGgPlusPassRedeemed(passAnalytics(result));
          this.setState({
            loading: false,
            phase: 'success',
            freeUntil: result.freeUntil,
            adjustmentType: result.adjustmentType || null,
            durationMonths: result.durationMonths || this.state.durationMonths,
            passId: result.passId || this.state.passId,
            sourceType: result.sourceType || this.state.sourceType,
            sourceId: result.sourceId || this.state.sourceId,
            referrerUserId: result.referrerUserId || this.state.referrerUserId,
          }, () => this.loadRegistry());
          return;
        }
        if (outcome === 'already_claimed') {
          this.setState({
            loading: false,
            phase: 'already',
            freeUntil: result.freeUntil,
            durationMonths: result.durationMonths || this.state.durationMonths,
          }, () => this.loadRegistry());
          return;
        }
        if (outcome === 'wait' || outcome === 'self' || outcome === 'unavailable' || outcome === 'error') {
          logGgPlusPassRedemptionFailed({
            ...passAnalytics(result && result.passId != null ? result : this.state),
            reason: outcome === 'error' ? 'error' : outcome,
          });
        } else {
          logGgPlusPassRedemptionFailed({ ...passAnalytics(this.state), reason: 'error' });
        }
        if (outcome === 'wait') {
          this.setState({ loading: false, phase: 'wait' });
          return;
        }
        if (outcome === 'self') {
          this.setState({ loading: false, phase: 'self' });
          return;
        }
        if (outcome === 'error') {
          this.setState({ loading: false, phase: 'error' });
          return;
        }
        this.setState({ loading: false, phase: 'unavailable' });
      })
      .catch(() => {
        logGgPlusPassRedemptionFailed({ ...passAnalytics(this.state), reason: 'error' });
        this.setState({ loading: false, phase: 'error' });
      });
  }

  loadRegistry() {
    if (!this.loggedIn()) return;
    GGApi.get_plans()
      .then((plans) => this.setState({ registry: registryAction(plans) }))
      .catch(() => this.setState({
        registry: { text: 'Set up your registry', path: '/add_account' },
      }));
  }

  handleClaim() {
    logGgPlusPassClaimStarted(passAnalytics(this.state));
    const token = this.token();
    if (!this.loggedIn()) {
      rememberClaimPending(token);
      const returnTo = `/plus/pass/${encodeURIComponent(token)}`;
      this.props.history.push(`/new_account?return_to=${encodeURIComponent(returnTo)}`);
      return;
    }
    this.redeem();
  }

  goToRegistry() {
    const registry = this.state.registry || { path: '/add_account' };
    this.props.history.push(registry.path);
  }

  renderShortCopy() {
    const end = formatMembershipDate(this.state.freeUntil);

    if (this.state.phase === 'success') {
      const onStripe = this.state.adjustmentType === 'coupon' || this.state.adjustmentType === 'trial_extension';
      return (
        <>
          <h1 className="gg_plus_card_headline">GG+ is on</h1>
          <p className="gg_plus_card_body">
            {onStripe
              ? `Your card and price stay. Billing resumes ${end ? `on ${end}` : 'when this complimentary stretch ends'}.`
              : `Complimentary GG+ runs until ${end || 'the date on your membership'}. Nothing renews on its own.`}
          </p>
        </>
      );
    }

    if (this.state.phase === 'already') {
      return (
        <>
          <h1 className="gg_plus_card_headline">You already claimed this pass</h1>
          <p className="gg_plus_card_body">
            {end
              ? `Complimentary GG+ runs until ${end}.`
              : 'This link was already claimed on your account.'}
          </p>
        </>
      );
    }

    if (this.state.phase === 'wait') {
      return (
        <>
          <h1 className="gg_plus_card_headline">This pass has to wait</h1>
          <p className="gg_plus_card_body">
            You already have a GG+ stretch in progress, so this link was not used.
          </p>
        </>
      );
    }

    if (this.state.phase === 'self') {
      return (
        <>
          <h1 className="gg_plus_card_headline">This is your share link</h1>
          <p className="gg_plus_card_body">
            You can’t claim your own pass. The link is still open for someone else.
          </p>
        </>
      );
    }

    if (this.state.phase === 'unavailable') {
      return (
        <>
          <h1 className="gg_plus_card_headline">This link is unavailable</h1>
          <p className="gg_plus_card_body">
            It may be used up, turned off, or no longer open.
          </p>
        </>
      );
    }

    return (
      <>
        <h1 className="gg_plus_card_headline">Something went wrong</h1>
        <p className="gg_plus_card_body">Try the link again in a moment.</p>
      </>
    );
  }

  renderShortAction() {
    if (this.state.phase === 'success' || this.state.phase === 'already') {
      const registry = this.state.registry || { text: 'Set up your registry', path: '/add_account' };
      return (
        <MasterButton
          buttonText={registry.text}
          handleButtonClick={this.goToRegistry}
          notSticky={true}
        />
      );
    }

    if (this.state.phase === 'error') {
      return (
        <MasterButton
          buttonText="Try again"
          handleButtonClick={() => this.openPass()}
          notSticky={true}
        />
      );
    }

    if (this.state.phase === 'wait' || this.state.phase === 'self' || this.state.phase === 'unavailable') {
      return (
        <p className="membership_fine_print">
          <Link to={this.loggedIn() ? '/home' : '/'} className="green_text">Back to Greatest Gift</Link>
        </p>
      );
    }

    return null;
  }

  render() {
    const offer = this.state.phase === 'offer';

    return (
      <div className={offer ? 'pass_offer_page' : 'membership_page'}>
        <main className={offer ? 'pass_offer_main' : 'membership_page_main membership_page_wrapper'}>
          <Helmet>
            <title>Claim GG+ | Greatest Gift</title>
            <meta name="robots" content="noindex" />
          </Helmet>
          {this.state.loading ? <GG_loader /> : (
            offer ? (
              <Plus_pass_offer
                durationMonths={this.state.durationMonths}
                giverLabel={this.state.giverLabel}
                onClaim={this.handleClaim}
              />
            ) : (
              <div className="membership_main_card">
                <img src={gg_plus_logo} alt="GG+" className="gg_plus_logo gg_plus_logo_large" />
                {this.renderShortCopy()}
                {this.renderShortAction()}
              </div>
            )
          )}
        </main>
      </div>
    );
  }
}

export default Plus_pass_page;
